<template>
  <div class="animated fadeIn">
    <div class="card shadow-sm border-0 mb-3">
      <div class="card-header bg-facebook text-white d-flex align-items-center justify-content-between py-2">
        <h6 class="mb-0"><font-awesome-icon icon="clock" /> Office Attendance</h6>
        <small class="text-white-50 text-uppercase font-weight-bold">Per payroll group</small>
      </div>
      <div class="card-body position-relative">
        <b-row align-v="center" class="loader" v-if="is_loading">
          <b-col class="text-center">
            <b-spinner></b-spinner>
            <div class="mt-2"><strong>Loading</strong></div>
          </b-col>
        </b-row>

        <b-form @submit.prevent="generateAttendance">
          <b-row align-v="end">
            <b-col md="3">
              <label class="small font-weight-bold text-uppercase">Payroll Group</label>
              <b-form-select v-model="form.payroll_group_id" :options="groupOptions" required size="sm"></b-form-select>
            </b-col>
            <b-col md="3">
              <label class="small font-weight-bold text-uppercase">Date From</label>
              <b-form-input type="date" v-model="form.date_from" required size="sm"></b-form-input>
            </b-col>
            <b-col md="3">
              <label class="small font-weight-bold text-uppercase">Date To</label>
              <b-form-input type="date" v-model="form.date_to" required size="sm"></b-form-input>
            </b-col>
            <b-col md="3">
              <b-button type="submit" variant="dark" block size="sm">
                <font-awesome-icon icon="search" /> Generate
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </div>
    </div>

    <div v-if="json_file.result.length > 0">
      <div class="card shadow-sm border-0 mb-3">
        <div class="card-body d-flex align-items-center justify-content-between flex-wrap">
          <div>
            <b-badge variant="dark" class="mr-2" v-if="json_file.payroll_group">{{ json_file.payroll_group.name }}</b-badge>
            <b-badge variant="info" class="mr-2">{{ json_file.formatted_from_date }}</b-badge>
            to
            <b-badge variant="info" class="ml-2">{{ json_file.formatted_to_date }}</b-badge>
          </div>
          <b-button variant="success" size="sm" @click="processImport">
            <font-awesome-icon icon="save" /> Process Time Log
          </b-button>
        </div>
      </div>

      <div class="card shadow-sm border-0 mb-3" v-for="(result, ri) in json_file.result" :key="ri">
        <div class="card-header bg-dark text-white py-2 d-flex align-items-center justify-content-between">
          <span><font-awesome-icon icon="calendar" /> {{ result.date.other_formats.format_1 }}</span>
          <b-badge v-if="result.holiday" :variant="result.holiday.type == 1 ? 'success' : 'warning'">
            {{ result.holiday.type_description }} &mdash; {{ result.holiday.name }}
({{ result.holiday.type == 1 ? '1 day holiday pay + duty' : 'duty + 30%' }})
          </b-badge>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive mb-0">
            <table class="table table-bordered table-sm mb-0 text-center align-middle">
              <thead class="thead-light">
                <tr>
                  <th class="text-left">Employee</th>
                  <th>AM IN</th>
                  <th>AM OUT</th>
                  <th>PM IN</th>
                  <th>PM OUT</th>
                  <th>OT IN</th>
                  <th>OT OUT</th>
                  <th>Daily Rate</th>
                  <th>Late (min)</th>
                  <th>Reg Hrs</th>
                  <th>OT Hrs</th>
                  <th>ND Hrs</th>
                  <th>Total</th>
                  <th>Include</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(log, li) in result.logs" :key="li" :class="log.is_incomplete ? 'table-warning' : { 'table-danger': log.is_deleted }">
                  <td class="text-left">
                    <div class="font-weight-bold text-uppercase">{{ log.other_details.manpower_details.full_name }}</div>
                    <b-badge variant="info">ID : {{ log.other_details.manpower_details.id_number }}</b-badge>
                    <b-badge variant="light" class="ml-1" v-if="log.other_details.shift_type">{{ log.other_details.shift_type.description }}</b-badge>
                    <b-button :variant="log.is_incomplete ? 'warning' : 'outline-primary'" size="sm" class="ml-1 py-0" @click="openEdit(log)" title="Edit punches"><font-awesome-icon icon="edit" /></b-button>
                    <div v-if="log.is_incomplete" class="small text-danger font-weight-bold mt-1">
                      <font-awesome-icon icon="info-circle" /> Incomplete &mdash; missing {{ (log.missing || []).join(', ') }}. Set the punch with the pencil; it is not processed until complete.
                    </div>
                  </td>
                  <!-- One cell per punch. Scanned punches carry a QR mark; punches
                       set by hand are flagged "manual" with the original scanned time. -->
                  <td v-for="p in punches" :key="p.key" :class="{ 'punch-manual': punchSource(log, p.key) === 'manual' }">
                    <img v-if="log.other_details[p.key + '_image']" :src="assets_path.OFFICE_LOGS_URL + log.other_details[p.key + '_image']" class="timelog-thumb d-block mx-auto mb-1" />
                    <span :class="{ 'font-weight-bold': punchSource(log, p.key) === 'manual' }">{{ timeText(log.other_details['formatted_' + p.key]) }}</span>
                    <div v-if="punchSource(log, p.key) === 'manual'" class="small text-warning text-nowrap" :title="auditTitle(log, p.key)">
                      <font-awesome-icon icon="pencil-alt" /> manual
                      <span class="text-muted" v-if="punchAudit(log, p.key).original">&middot; scan <s>{{ punchAudit(log, p.key).original }}</s></span>
                      <span class="text-muted" v-else>&middot; added</span>
                    </div>
                    <div v-else-if="punchSource(log, p.key) === 'removed'" class="small text-danger text-nowrap" :title="auditTitle(log, p.key)">
                      <font-awesome-icon icon="pencil-alt" /> removed &middot; scan <s>{{ punchAudit(log, p.key).original }}</s>
                    </div>
                    <div v-else-if="punchSource(log, p.key) === 'kiosk'" class="small text-muted" title="Scanned on the kiosk">
                      <font-awesome-icon icon="qrcode" />
                    </div>
                  </td>
                  <td class="text-right">
                    {{ money(log.daily_rate) }}
                    <div class="small text-muted text-nowrap" v-if="log.duty_hours">/ {{ log.duty_hours }}h</div>
                  </td>
                  <td :class="log.late_minutes > 0 ? 'text-danger font-weight-bold' : ''">{{ val(log.late_minutes) }}</td>
                  <td>
                    {{ val(log.regular_hours) }}
                    <b-badge v-if="log.holiday_type == 1" variant="success">+1 day holiday pay</b-badge>
                    <b-badge v-else-if="log.holiday_type == 2" variant="warning">+30%</b-badge>
                    <div v-if="log.am_extension_hours > 0" class="small text-muted text-nowrap" title="AM extension: time past Break Start credited toward the required duty hours (shift setting)">
                      incl. {{ log.am_extension_hours }}h AM extension
                    </div>
                    <div v-if="log.ot_fill_hours > 0" class="small text-muted text-nowrap" title="Required duty hours are fulfilled first: this much of the OT time was counted as regular hours">
                      incl. {{ log.ot_fill_hours }}h from OT
                    </div>
                  </td>
                  <td>{{ val(log.ot_hours) }}</td>
                  <td>{{ val(log.nd_hours) }}</td>
                  <td class="font-weight-bold text-right">{{ log.is_incomplete ? '--' : money(log.total) }}</td>
                  <td>
                    <b-form-checkbox
                      :checked="!log.is_deleted"
                      :disabled="!!log.is_incomplete"
                      switch
                      @change="val => log.is_deleted = val ? 0 : 1"
                    ></b-form-checkbox>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="text-right mb-4">
        <b-button variant="success" size="sm" @click="processImport">
          <font-awesome-icon icon="save" /> Process Time Log
        </b-button>
      </div>
    </div>

    <b-alert v-else-if="generated" show variant="warning">No records found for the selected payroll group and range.</b-alert>

    <b-modal v-model="editModal" centered size="md" :hide-footer="true" :title="editForm.name ? ('Edit Times — ' + editForm.name) : 'Edit Times'">
      <p class="small text-muted">{{ editForm.date }} — leave a field blank to clear that punch. Every change is recorded with your name; the original scanned time stays visible on the attendance page.</p>
      <b-form @submit.prevent="saveTimes">
        <b-row>
          <b-col cols="6" v-for="p in punches" :key="p.key">
            <b-form-group :label="p.label">
              <b-form-input type="time" v-model="editForm[p.key]"></b-form-input>
              <small class="d-block mt-1" :class="editAudit[p.key] && editAudit[p.key].source === 'manual' ? 'text-warning' : 'text-muted'">{{ editHint(p.key) }}</small>
            </b-form-group>
          </b-col>
        </b-row>
        <b-button type="submit" variant="success" block><font-awesome-icon icon="save" /> Save Times</b-button>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
  import SwalMixin from '@/views/mixins/Swal.js'
  import { assets_path } from '@/config'

  export default {
    name: 'OfficeAttendance',
    mixins: [SwalMixin],
    data() {
      return {
        assets_path: assets_path,
        is_loading: false,
        generated: false,
        groups: [],
        form: { payroll_group_id: null, date_from: '', date_to: '' },
        json_file: { payroll_group_id: null, payroll_group: null, from_date: '', to_date: '', formatted_from_date: '', formatted_to_date: '', result: [] },
        editModal: false,
        editForm: { id: null, name: '', date: '', am_in: '', am_out: '', pm_in: '', pm_out: '', ot_in: '', ot_out: '' },
        editAudit: {},
        punches: [
          { key: 'am_in', label: 'AM In' },
          { key: 'am_out', label: 'AM Out' },
          { key: 'pm_in', label: 'PM In' },
          { key: 'pm_out', label: 'PM Out' },
          { key: 'ot_in', label: 'OT In' },
          { key: 'ot_out', label: 'OT Out' },
        ],
      }
    },
    computed: {
      groupOptions() {
        return [{ value: null, text: 'Select payroll group', disabled: true }]
          .concat(this.groups.map(g => ({ value: g.id, text: g.name })));
      }
    },
    created() {
      this.getGroups();
    },
    methods: {
      getGroups() {
        this.axios.get('payroll-groups/options').then(response => {
          this.groups = response.data || [];
          // Remember the last group used on this page; default to the only/first one.
          let remembered = null;
          try { remembered = Number(sessionStorage.getItem('office_attendance_group')) || null; } catch (e) { remembered = null; }
          if (remembered && this.groups.find(g => g.id === remembered)) this.form.payroll_group_id = remembered;
          else if (this.groups.length === 1) this.form.payroll_group_id = this.groups[0].id;
        }).catch(error => console.log(error));
      },
      timeText(formatted) {
        if (!formatted || formatted.default === '') return '--';
        return formatted.other_formats.format_1;
      },
      val(v) {
        return (v === null || v === undefined) ? '--' : v;
      },
      hm(datetime) {
        return datetime ? String(datetime).substring(11, 16) : '';
      },
      // Audit info for one punch of a row: { source, original, edited_by, edited_at, edits }.
      punchAudit(log, key) {
        const audit = log.other_details && log.other_details.punch_audit;
        return (audit && audit[key]) ? audit[key] : {};
      },
      punchSource(log, key) {
        return this.punchAudit(log, key).source || null;
      },
      auditTitle(log, key) {
        const a = this.punchAudit(log, key);
        if (!a.source || a.source === 'kiosk') return 'Scanned on the kiosk';
        let text = (a.source === 'removed' ? 'Removed' : 'Set') + ' manually';
        if (a.edited_by) text += ' by ' + a.edited_by;
        if (a.edited_at) text += ' on ' + a.edited_at;
        if (a.edits > 1) text += ' (' + a.edits + ' edits)';
        text += a.original ? '. Original scan: ' + a.original : '. No scan existed for this punch.';
        return text;
      },
      editHint(key) {
        const a = this.editAudit[key] || {};
        if (a.source === 'kiosk') return 'Scanned on the kiosk';
        if (a.source === 'manual') return 'Manual' + (a.original ? ' (scan was ' + a.original + ')' : ' (added, no scan)') + (a.edited_by ? ' by ' + a.edited_by : '');
        if (a.source === 'removed') return 'Removed manually (scan was ' + a.original + ')';
        return 'No punch';
      },
      openEdit(log) {
        const o = log.other_details;
        this.editAudit = o.punch_audit || {};
        this.editForm = {
          id: o.id,
          name: o.manpower_details.full_name,
          date: o.formatted_date ? o.formatted_date.other_formats.format_1 : (o.date || '').substring(0, 10),
          am_in: this.hm(o.am_in),
          am_out: this.hm(o.am_out),
          pm_in: this.hm(o.pm_in),
          pm_out: this.hm(o.pm_out),
          ot_in: this.hm(o.ot_in),
          ot_out: this.hm(o.ot_out),
        };
        this.editModal = true;
      },
      saveTimes() {
        this.is_loading = true;
        this.axios.post('office-timelog-logs/update-times', this.editForm).then(() => {
          this.editModal = false;
          this.swalTransactionCompleted();
          this.generateAttendance();
        }).catch(error => {
          this.is_loading = false;
          this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Something went wrong.';
          this.swalRequestError();
        });
      },
      money(value) {
        const n = Number(value);
        if (isNaN(n)) return value;
        return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      resetResult() {
        this.json_file = { payroll_group_id: null, payroll_group: null, from_date: '', to_date: '', formatted_from_date: '', formatted_to_date: '', result: [] };
      },
      generateAttendance() {
        if (!this.form.payroll_group_id) return;
        try { sessionStorage.setItem('office_attendance_group', String(this.form.payroll_group_id)); } catch (e) { /* ignore */ }
        this.is_loading = true;
        this.generated = false;
        this.axios.post('office-timelog-logs/generate', this.form).then(response => {
          this.json_file = response.data;
          this.generated = true;
          this.is_loading = false;
        }).catch(error => {
          this.resetResult();
          this.generated = true;
          this.is_loading = false;
          if (!(error.response && error.response.status === 404)) {
            this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Something went wrong.';
            this.swalRequestError();
          }
        });
      },
      processImport() {
        this.swalConfirmSubmit((data) => {
          if (data.value) {
            this.is_loading = true;
            this.axios.post('office-timelog-logs/process', this.json_file).then(() => {
              this.is_loading = false;
              this.swal_transaction_completed_data.text = 'Time log processed. It is now available under For Approval.';
              this.swalTransactionCompleted();
              this.resetResult();
              this.generated = false;
            }).catch(error => {
              this.is_loading = false;
              this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Something went wrong.';
              this.swalRequestError();
            });
          }
        });
      }
    }
  }
</script>

<style scoped>
  .timelog-thumb {
    width: 42px;
    height: 42px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #dee2e6;
  }
</style>
